import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
  type ReactNode,
} from "react";
import { useLanguage } from "~/hooks/useLanguage";
import { useCurrency } from "~/hooks/useCurrency";
import useTracking from "~/hooks/useTracking";
import { useFiatHistory } from "~/hooks/useFiatHistory";
import type { FiatRateHistory } from "~/calculations/genericCalculations/convertCurrencyOnDate";
import { CashflowMixpanelEvents } from "../assetsChart/mixpanel/mixpanelEvents";
import type {
  BudgetCategoryMap,
  CategoryData,
  OptionInterface,
  Transaction,
  monthOption,
} from "./chartDataFunctions";
import {
  addCategoryAndValue,
  applyConnectedCardFilter,
  buildBudgetCategoryMap,
  enrichTransactions,
  generateTransactionGroups,
  getDateByFuncType,
  getMonthsFromTransactions,
  translateCategoryName,
} from "./chartDataFunctions";
import { cashflowCharts } from "~/translations/cashflow/cashflowCharts";
import { cashflowDateType, transacitonActionType } from "./cashflowEnums";
import type { TransactionRule } from "~/types/global.types";
import type {
  BudgetCategory,
  BudgetCategoryGroup,
  BudgetCategoryCodeMapping,
} from "~/types/budget.types";
import type { GroupedOption } from "./components/cashflowAccountsSelection";
import type { DateRange } from "./components/cashflowTimeSelection";
import {
  createLabelFromDateRange,
  readStoredCashflowDateRange,
  resolveCashflowDateRange,
} from "./components/cashflowTimeSelection";
import {
  accountSelectionKey,
  lookupPreparedEnrichment,
  storePreparedEnrichment,
} from "./cashflowClientCache";
import { stringToColor } from "./transactionSummaryChart";
import localforage from "localforage";
import { includeDefaultAccountSelections } from "~/utils/manualAccountSelection";

// ─── Core state managed by the reducer ───

interface CashflowCoreState {
  transactions: Transaction[];
  transactionRules: TransactionRule[];
  loading: boolean;
  fetchError: boolean;
  budgetGroups: BudgetCategoryGroup[];
  budgetCategories: BudgetCategory[];
  budgetMappings: BudgetCategoryCodeMapping[];
  expensesCategory: string;
  incomeCategory: string;
  expensesCategoryType: string;
  incomeCategoryType: string;
  selectedAccounts: OptionInterface[];
  transactionDateRange: DateRange | undefined;
  dateRefFunctionType: string;
  cycleDay: number;
}

// ─── Action types ───

export type CashflowAction =
  | { type: "SET_TRANSACTIONS"; payload: Transaction[] }
  | { type: "SET_RULES"; payload: TransactionRule[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_FETCH_ERROR"; payload: boolean }
  | {
      type: "SET_BUDGET_DATA";
      payload: {
        groups: BudgetCategoryGroup[];
        categories: BudgetCategory[];
        mappings: BudgetCategoryCodeMapping[];
      };
    }
  | { type: "ADD_RULE"; payload: TransactionRule }
  | { type: "REMOVE_RULE"; payload: TransactionRule }
  | {
      type: "SET_CATEGORY_FILTER";
      payload: { column: "expenses" | "income"; category: string };
    }
  | {
      type: "SET_CATEGORY_TYPE";
      payload: { column: "expenses" | "income"; categoryType: string };
    }
  | { type: "SET_DATE_RANGE"; payload: DateRange }
  | { type: "SET_SELECTED_ACCOUNTS"; payload: OptionInterface[] }
  | { type: "SET_DATE_TYPE"; payload: string }
  | { type: "SET_CYCLE_DAY"; payload: number }
  | { type: "RESET_CATEGORY_FILTERS" };

// ─── Reducer ───

function sameAccountValues(
  left: OptionInterface[],
  right: OptionInterface[]
): boolean {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i]?.value !== right[i]?.value) return false;
  }
  return true;
}

function findRuleIndex(
  rules: TransactionRule[],
  rule: TransactionRule
): number {
  return rules.findIndex(
    (r) =>
      (rule.id && r.id === rule.id) ||
      (r.rule_type === rule.rule_type &&
        r.rule_field === rule.rule_field &&
        r.rule_value === rule.rule_value &&
        r.action_type === rule.action_type &&
        r.action_value === rule.action_value)
  );
}

function cashflowReducer(
  state: CashflowCoreState,
  action: CashflowAction
): CashflowCoreState {
  switch (action.type) {
    case "SET_TRANSACTIONS":
      if (state.transactions === action.payload) return state;
      return { ...state, transactions: action.payload };

    case "SET_RULES":
      if (state.transactionRules === action.payload) return state;
      return { ...state, transactionRules: action.payload };

    case "SET_LOADING":
      if (state.loading === action.payload) return state;
      return { ...state, loading: action.payload };

    case "SET_FETCH_ERROR":
      if (state.fetchError === action.payload) return state;
      return { ...state, fetchError: action.payload };

    case "SET_BUDGET_DATA":
      if (
        state.budgetGroups === action.payload.groups &&
        state.budgetCategories === action.payload.categories &&
        state.budgetMappings === action.payload.mappings
      ) {
        return state;
      }
      return {
        ...state,
        budgetGroups: action.payload.groups,
        budgetCategories: action.payload.categories,
        budgetMappings: action.payload.mappings,
      };

    case "ADD_RULE": {
      const idx = findRuleIndex(state.transactionRules, action.payload);
      const updated =
        idx > -1
          ? state.transactionRules.map((r, i) =>
              i === idx ? { ...action.payload, transactions: undefined } : r
            )
          : [...state.transactionRules, action.payload];
      return { ...state, transactionRules: updated };
    }

    case "REMOVE_RULE": {
      const idx = findRuleIndex(state.transactionRules, action.payload);
      if (idx === -1) return state;
      return {
        ...state,
        transactionRules: state.transactionRules.filter((_, i) => i !== idx),
      };
    }

    case "SET_CATEGORY_FILTER":
      if (action.payload.column === "expenses") {
        if (state.expensesCategory === action.payload.category) return state;
        return { ...state, expensesCategory: action.payload.category };
      }
      if (state.incomeCategory === action.payload.category) return state;
      return { ...state, incomeCategory: action.payload.category };

    case "SET_CATEGORY_TYPE":
      return action.payload.column === "expenses"
        ? { ...state, expensesCategoryType: action.payload.categoryType }
        : { ...state, incomeCategoryType: action.payload.categoryType };

    case "SET_DATE_RANGE": {
      const current = state.transactionDateRange;
      const next = action.payload;
      if (
        current &&
        current.startDate.getTime() === next.startDate.getTime() &&
        current.endDate.getTime() === next.endDate.getTime()
      ) {
        return state;
      }
      return { ...state, transactionDateRange: next };
    }

    case "SET_SELECTED_ACCOUNTS":
      if (sameAccountValues(state.selectedAccounts, action.payload)) {
        return state;
      }
      return { ...state, selectedAccounts: action.payload };

    case "SET_DATE_TYPE":
      return { ...state, dateRefFunctionType: action.payload };

    case "SET_CYCLE_DAY":
      return { ...state, cycleDay: action.payload };

    case "RESET_CATEGORY_FILTERS": {
      const allLabel = cashflowCharts["HEB"].Categories.All;
      return {
        ...state,
        expensesCategory: allLabel,
        incomeCategory: allLabel,
      };
    }

    default:
      return state;
  }
}

// ─── Context value ───

export interface CashflowStateValue {
  transactions: Transaction[];
  transactionRules: TransactionRule[];
  loading: boolean;
  fetchError: boolean;
  onRetry?: () => void;
  budgetGroups: BudgetCategoryGroup[];
  budgetCategories: BudgetCategory[];
  budgetMappings: BudgetCategoryCodeMapping[];
  budgetMap: BudgetCategoryMap | undefined;
  expensesCategory: string;
  incomeCategory: string;
  expensesCategoryType: string;
  incomeCategoryType: string;
  selectedAccounts: OptionInterface[];
  transactionDateRange: DateRange | undefined;
  dateRefFunctionType: string;
  cycleDay: number;
  enrichedTransactions: Transaction[];
  currentMonthTransactions: Transaction[];
  expenseTransactions: Transaction[];
  incomeTransactions: Transaction[];
  months: monthOption[];
  monthLabel: string;
  transactionAccounts: GroupedOption[];
  categoryList: CategoryData[];
  /** Daily fiat rate history for transaction-date FX (null while loading/unavailable). */
  fiatHistory: FiatRateHistory | null;
}

const CashflowStateContext = createContext<CashflowStateValue | null>(null);
const CashflowDispatchContext = createContext<Dispatch<CashflowAction> | null>(
  null
);

function cloneTransactionForEnrichment(transaction: Transaction): Transaction {
  return {
    ...transaction,
    amount: {
      originalAmount: { ...transaction.amount.originalAmount },
      chargedAmount: { ...transaction.amount.chargedAmount },
    },
    description: { ...transaction.description },
    category: { ...transaction.category },
    changedCategory: transaction.changedCategory
      ? { ...transaction.changedCategory }
      : undefined,
    date: { ...transaction.date },
    rules: transaction.rules ? [...transaction.rules] : [],
  };
}

// ─── Provider ───

interface CashflowProviderProps {
  children: ReactNode;
  initialTransactions: Transaction[];
  initialRules: TransactionRule[];
  initialLoading: boolean;
  initialBudgetGroups: BudgetCategoryGroup[];
  initialBudgetCategories: BudgetCategory[];
  initialBudgetMappings: BudgetCategoryCodeMapping[];
  defaultCategoryView?: "main" | "sub";
  initialFetchError?: boolean;
  onRetry?: () => void;
}

export function CashflowProvider({
  children,
  initialTransactions,
  initialRules,
  initialLoading,
  initialBudgetGroups,
  initialBudgetCategories,
  initialBudgetMappings,
  defaultCategoryView = "main",
  initialFetchError = false,
  onRetry,
}: CashflowProviderProps) {
  const { lang } = useLanguage();
  const { symbol: currencySymbol } = useCurrency();
  const tracking = useTracking();
  const documentRef = useRef<Document | null>(null);
  const rulesPropSyncRef = useRef(false);
  const txPropSyncRef = useRef(false);

  useEffect(() => {
    documentRef.current = document;
  }, []);

  const initialState: CashflowCoreState = useMemo(
    () => ({
      transactions: initialTransactions,
      transactionRules: initialRules,
      loading: initialLoading,
      fetchError: initialFetchError,
      budgetGroups: initialBudgetGroups,
      budgetCategories: initialBudgetCategories,
      budgetMappings: initialBudgetMappings,
      expensesCategory: cashflowCharts[lang].Categories.All,
      incomeCategory: cashflowCharts[lang].Categories.All,
      expensesCategoryType:
        defaultCategoryView === "sub"
          ? cashflowCharts[lang].CategoryTypes.Sub
          : cashflowCharts[lang].CategoryTypes.Main,
      incomeCategoryType:
        defaultCategoryView === "sub"
          ? cashflowCharts[lang].CategoryTypes.Sub
          : cashflowCharts[lang].CategoryTypes.Main,
      selectedAccounts:
        typeof window !== "undefined"
          ? JSON.parse(window.localStorage.getItem("selectedAccounts") || "[]")
          : [],
      transactionDateRange:
        typeof window !== "undefined"
          ? readStoredCashflowDateRange()
          : undefined,
      dateRefFunctionType:
        typeof window !== "undefined" &&
        window.localStorage.getItem("dateType") === cashflowDateType.VALUE_DATE
          ? cashflowDateType.VALUE_DATE
          : cashflowDateType.TRANSACTION_DATE,
      cycleDay:
        typeof window !== "undefined"
          ? parseInt(window.localStorage.getItem("cycleDay") || "0", 10)
          : 0,
    }),
    // Only used for the initial useReducer call
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [state, dispatch] = useReducer(cashflowReducer, initialState);

  // ── Sync incoming prop changes into state ──

  const transactionsRef = useRef(state.transactions);
  transactionsRef.current = state.transactions;
  const rulesStateRef = useRef(state.transactionRules);
  rulesStateRef.current = state.transactionRules;
  const loadingRef = useRef(state.loading);
  loadingRef.current = state.loading;
  const fetchErrorRef = useRef(state.fetchError);
  fetchErrorRef.current = state.fetchError;
  const budgetRef = useRef({
    groups: state.budgetGroups,
    categories: state.budgetCategories,
    mappings: state.budgetMappings,
  });
  budgetRef.current = {
    groups: state.budgetGroups,
    categories: state.budgetCategories,
    mappings: state.budgetMappings,
  };

  useEffect(() => {
    if (initialTransactions === transactionsRef.current) return;
    txPropSyncRef.current = true;
    dispatch({ type: "SET_TRANSACTIONS", payload: initialTransactions });
  }, [initialTransactions]);

  useEffect(() => {
    if (initialRules === rulesStateRef.current) return;
    rulesPropSyncRef.current = true;
    dispatch({ type: "SET_RULES", payload: initialRules });
  }, [initialRules]);

  useEffect(() => {
    if (initialLoading === loadingRef.current) return;
    dispatch({ type: "SET_LOADING", payload: initialLoading });
  }, [initialLoading]);

  useEffect(() => {
    if (initialFetchError === fetchErrorRef.current) return;
    dispatch({ type: "SET_FETCH_ERROR", payload: initialFetchError });
  }, [initialFetchError]);

  useEffect(() => {
    const current = budgetRef.current;
    if (
      initialBudgetGroups === current.groups &&
      initialBudgetCategories === current.categories &&
      initialBudgetMappings === current.mappings
    ) {
      return;
    }
    dispatch({
      type: "SET_BUDGET_DATA",
      payload: {
        groups: initialBudgetGroups,
        categories: initialBudgetCategories,
        mappings: initialBudgetMappings,
      },
    });
  }, [initialBudgetGroups, initialBudgetCategories, initialBudgetMappings]);

  // ── Derived: budget map ──

  const budgetMap: BudgetCategoryMap | undefined = useMemo(() => {
    if (!state.budgetCategories?.length || !state.budgetGroups?.length)
      return undefined;
    return buildBudgetCategoryMap(
      lang,
      state.budgetGroups,
      state.budgetCategories,
      state.budgetMappings || []
    );
  }, [lang, state.budgetGroups, state.budgetCategories, state.budgetMappings]);

  // ── Derived: enriched transactions ──

  const accountKey = accountSelectionKey(state.selectedAccounts);

  const enrichedTransactions = useMemo(() => {
    const query = {
      transactions: state.transactions,
      rules: state.transactionRules,
      lang,
      expensesCategoryType: state.expensesCategoryType,
      incomeCategoryType: state.incomeCategoryType,
      accountKey,
      budgetGroups: state.budgetGroups,
      budgetCategories: state.budgetCategories,
      budgetMappings: state.budgetMappings,
    };
    const prepared = lookupPreparedEnrichment(query);
    if (prepared) return prepared;

    const base = (state.transactions || []).map(cloneTransactionForEnrichment);
    enrichTransactions(
      lang,
      base,
      state.expensesCategoryType,
      state.incomeCategoryType,
      state.transactionRules,
      budgetMap
    );
    applyConnectedCardFilter(base, state.selectedAccounts);
    storePreparedEnrichment(query, base);
    return base;
  }, [
    state.transactions,
    state.transactionRules,
    state.expensesCategoryType,
    state.incomeCategoryType,
    state.selectedAccounts,
    state.budgetGroups,
    state.budgetCategories,
    state.budgetMappings,
    accountKey,
    budgetMap,
    lang,
  ]);

  // ── Persist enriched data to localforage (for dashboard monthly chart) ──

  const enrichedTxRef = useRef(enrichedTransactions);
  enrichedTxRef.current = enrichedTransactions;
  const langRef = useRef(lang);
  langRef.current = lang;
  const currencySymbolRef = useRef(currencySymbol);
  currencySymbolRef.current = currencySymbol;

  useEffect(() => {
    const idle =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 200) as unknown as number;
    const cancel =
      typeof cancelIdleCallback === "function"
        ? cancelIdleCallback
        : clearTimeout;

    const id = idle(() => {
      const enriched = addCategoryAndValue(
        langRef.current,
        enrichedTxRef.current.map((t) => ({ ...t })),
        currencySymbolRef.current
      ) as unknown as Transaction[];
      void localforage.setItem("enrichedTransactionData", enriched);
    });
    return () => cancel(id as any);
  }, [enrichedTransactions, lang, currencySymbol]);

  // ── Derived: months + the range used for the first paint ──

  const months = useMemo(
    () =>
      getMonthsFromTransactions(
        lang,
        state.dateRefFunctionType,
        state.transactions
      ),
    [lang, state.dateRefFunctionType, state.transactions]
  );

  const effectiveDateRange =
    state.transactionDateRange ??
    resolveCashflowDateRange(lang, months, state.cycleDay);

  // Apply the saved/default month before paint so totals don't flash
  // "everything" and then snap to the current month.
  if (!state.transactionDateRange && effectiveDateRange) {
    dispatch({ type: "SET_DATE_RANGE", payload: effectiveDateRange });
  }

  // ── Derived: current month transactions ──

  const currentMonthTransactions = useMemo(() => {
    let filtered = enrichedTransactions.filter((t) => t.type !== "LOAN");
    if (state.selectedAccounts.length > 0) {
      filtered = filtered.filter((t) => {
        if (!t.accountNumber) return false;
        return state.selectedAccounts.some(
          (group) => t.accountNumber === group.value
        );
      });
    } else {
      return filtered;
    }
    if (!effectiveDateRange) return filtered;
    return filtered.filter((t) => {
      const transactionDate = getDateByFuncType(state.dateRefFunctionType, t);
      return (
        transactionDate >= effectiveDateRange.startDate &&
        transactionDate <= effectiveDateRange.endDate
      );
    });
  }, [
    enrichedTransactions,
    state.selectedAccounts,
    effectiveDateRange,
    state.dateRefFunctionType,
  ]);

  const expenseTransactions = useMemo(
    () =>
      currentMonthTransactions.filter(
        (t: any) => t.amount.chargedAmount.amount < 0
      ),
    [currentMonthTransactions]
  );

  const incomeTransactions = useMemo(
    () =>
      currentMonthTransactions.filter(
        (t: any) => t.amount.chargedAmount.amount > 0
      ),
    [currentMonthTransactions]
  );

  // ── Derived: monthLabel, transactionAccounts, categoryList ──

  const startTime = effectiveDateRange?.startDate?.getTime();
  const endTime = effectiveDateRange?.endDate?.getTime();

  const monthLabel = useMemo(
    () =>
      createLabelFromDateRange(
        effectiveDateRange?.startDate,
        effectiveDateRange?.endDate,
        lang
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTime, endTime, lang]
  );

  // Intentionally only recompute when the transaction count changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const transactionAccounts: GroupedOption[] = useMemo(
    () => generateTransactionGroups(lang, state.transactions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.transactions.length, lang]
  );

  const allTransactionAccountOptions = useMemo(
    () => transactionAccounts.flatMap((group) => group.options),
    [transactionAccounts]
  );

  const categoryList: CategoryData[] = useMemo(() => {
    const categories = enrichedTransactions.map(
      (t) => t.calculatedCategory || ""
    );
    const translated = categories.map(
      (c) =>
        translateCategoryName(lang, c) ||
        c ||
        cashflowCharts[lang].Categories.Uncategorized
    );
    const categorySet = [...new Set(translated)];
    return categorySet
      .map((c) => ({
        label: c,
        value: c,
        color: stringToColor(c),
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, lang === "ENG" ? "en" : "he")
      );
  }, [enrichedTransactions, lang]);

  // ── Auto-select accounts on first load ──

  useEffect(() => {
    const cached = JSON.parse(
      window.localStorage.getItem("selectedAccounts") || "[]"
    );
    if (allTransactionAccountOptions.length === 0) return;

    const availableValues = new Set(
      allTransactionAccountOptions.map((account) => account.value)
    );
    const validCached = Array.isArray(cached)
      ? cached.filter((account) => availableValues.has(account.value))
      : [];
    const baseSelection =
      validCached.length > 0 ? validCached : allTransactionAccountOptions;
    const selectionWithDefaults = includeDefaultAccountSelections(
      baseSelection,
      allTransactionAccountOptions,
      window.localStorage
    );

    dispatch({
      type: "SET_SELECTED_ACCOUNTS",
      payload: selectionWithDefaults,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTransactionAccountOptions]);

  // ── Reset category filters on certain state changes ──

  useEffect(() => {
    dispatch({
      type: "SET_CATEGORY_FILTER",
      payload: {
        column: "expenses",
        category: cashflowCharts[lang].Categories.All,
      },
    });
    dispatch({
      type: "SET_CATEGORY_FILTER",
      payload: {
        column: "income",
        category: cashflowCharts[lang].Categories.All,
      },
    });
  }, [
    state.selectedAccounts,
    state.expensesCategoryType,
    state.incomeCategoryType,
    state.transactionDateRange,
    lang,
  ]);

  // ── Persistence side-effects ──

  useEffect(() => {
    window.localStorage.setItem(
      "transactionRules",
      JSON.stringify(state.transactionRules)
    );
  }, [state.transactionRules]);

  useEffect(() => {
    if (state.selectedAccounts.length > 0) {
      window.localStorage.setItem(
        "selectedAccounts",
        JSON.stringify(state.selectedAccounts)
      );
    }
  }, [state.selectedAccounts]);

  useEffect(() => {
    if (state.transactionDateRange) {
      window.localStorage.setItem(
        "transactionDateRange",
        JSON.stringify(state.transactionDateRange)
      );
    }
  }, [state.transactionDateRange]);

  useEffect(() => {
    window.localStorage.setItem("dateType", state.dateRefFunctionType);
  }, [state.dateRefFunctionType]);

  useEffect(() => {
    window.localStorage.setItem("cycleDay", String(state.cycleDay));
  }, [state.cycleDay]);

  useEffect(() => {
    localStorage.setItem("categoryList", JSON.stringify(categoryList));
  }, [categoryList]);

  // Build a view-mode-independent sub-category list from raw transaction data
  // so the rule form always has the real OF/custom sub-categories available,
  // regardless of whether the cashflow view is in grouped (מקובץ) or sub mode.
  // Includes custom categories from CATEGORY_CHANGE rules (e.g. "wolt").
  // Excludes entries where the transaction has no distinct sub (sub === main).
  const { subCategoryList, subToMainMap } = useMemo(() => {
    const names = new Set<string>();
    const mapping: Record<string, string> = {};

    for (const t of enrichedTransactions) {
      const ruleOverride = (t.rules ?? []).find(
        (r) => r.action_type === transacitonActionType.CATEGORY_CHANGE
      )?.action_value;

      if (ruleOverride) {
        names.add(ruleOverride);
        continue;
      }

      const sub = t.changedCategory?.sub || t.category?.sub || "";
      const main = t.changedCategory?.main || t.category?.main || "";

      if (sub && sub !== main) {
        const translatedSub =
          translateCategoryName(lang, sub) ||
          sub ||
          cashflowCharts[lang].Categories.Uncategorized;
        const translatedMain = translateCategoryName(lang, main) || main;
        names.add(sub);
        if (translatedMain && !mapping[translatedSub]) {
          mapping[translatedSub] = translatedMain;
        }
        if (translatedMain && !mapping[sub]) {
          mapping[sub] = translatedMain;
        }
      }
    }

    return {
      subCategoryList: [...names]
        .map((c) => ({
          label:
            translateCategoryName(lang, c) ||
            c ||
            cashflowCharts[lang].Categories.Uncategorized,
          value: c,
          color: stringToColor(c),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, lang === "ENG" ? "en" : "he")
        ) as CategoryData[],
      subToMainMap: mapping,
    };
  }, [enrichedTransactions, lang]);

  useEffect(() => {
    localStorage.setItem("subCategoryList", JSON.stringify(subCategoryList));
    localStorage.setItem("subToMainMap", JSON.stringify(subToMainMap));
  }, [subCategoryList, subToMainMap]);

  // ── DOM events for BudgetContainer compatibility ──

  const prevRulesRef = useRef(state.transactionRules);
  useEffect(() => {
    if (prevRulesRef.current !== state.transactionRules) {
      prevRulesRef.current = state.transactionRules;
      if (rulesPropSyncRef.current) {
        rulesPropSyncRef.current = false;
      } else {
        documentRef.current?.dispatchEvent(
          new Event("transactionRulesUpdated")
        );
      }
    }
  }, [state.transactionRules]);

  const prevTransactionsRef = useRef(state.transactions);
  useEffect(() => {
    if (prevTransactionsRef.current !== state.transactions) {
      prevTransactionsRef.current = state.transactions;
      if (txPropSyncRef.current) {
        txPropSyncRef.current = false;
      } else {
        documentRef.current?.dispatchEvent(new Event("transactionsUpdated"));
      }
    }
  }, [state.transactions]);

  // ── Tracking for rules ──

  const rulesRef = useRef(state.transactionRules);
  rulesRef.current = state.transactionRules;

  const trackingDispatch: Dispatch<CashflowAction> = useCallback(
    (action: CashflowAction) => {
      if (action.type === "ADD_RULE") {
        const existing = findRuleIndex(
          rulesRef.current,
          action.payload as TransactionRule
        );
        if (existing === -1) {
          tracking.track(CashflowMixpanelEvents.ADD_RULE);
        }
      } else if (action.type === "REMOVE_RULE") {
        tracking.track(CashflowMixpanelEvents.REMOVE_RULE);
      } else if (action.type === "SET_SELECTED_ACCOUNTS") {
        tracking.track(CashflowMixpanelEvents.SWITCH_TRANSACTION_GROUP);
      }
      dispatch(action);
    },
    [tracking]
  );

  // ── Derived: foreign currencies + their daily rate history ──

  const foreignCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    for (const transaction of state.transactions) {
      const currency = transaction.amount?.chargedAmount?.currency;
      if (currency && currency !== "ILS") currencies.add(currency);
    }
    return Array.from(currencies);
  }, [state.transactions]);

  const { history: fiatHistory } = useFiatHistory(foreignCurrencies);

  // ── Assemble context value ──

  const contextValue: CashflowStateValue = useMemo(
    () => ({
      transactions: state.transactions,
      transactionRules: state.transactionRules,
      loading: state.loading,
      fetchError: state.fetchError,
      onRetry,
      budgetGroups: state.budgetGroups,
      budgetCategories: state.budgetCategories,
      budgetMappings: state.budgetMappings,
      budgetMap,
      expensesCategory: state.expensesCategory,
      incomeCategory: state.incomeCategory,
      expensesCategoryType: state.expensesCategoryType,
      incomeCategoryType: state.incomeCategoryType,
      selectedAccounts: state.selectedAccounts,
      transactionDateRange: effectiveDateRange,
      dateRefFunctionType: state.dateRefFunctionType,
      cycleDay: state.cycleDay,
      enrichedTransactions,
      currentMonthTransactions,
      expenseTransactions,
      incomeTransactions,
      months,
      monthLabel,
      transactionAccounts,
      categoryList,
      fiatHistory,
    }),
    [
      state,
      effectiveDateRange,
      budgetMap,
      enrichedTransactions,
      currentMonthTransactions,
      expenseTransactions,
      incomeTransactions,
      months,
      monthLabel,
      transactionAccounts,
      categoryList,
      fiatHistory,
      onRetry,
    ]
  );

  return (
    <CashflowStateContext.Provider value={contextValue}>
      <CashflowDispatchContext.Provider value={trackingDispatch}>
        {children}
      </CashflowDispatchContext.Provider>
    </CashflowStateContext.Provider>
  );
}

// ─── Hooks ───

export function useCashflow(): CashflowStateValue {
  const ctx = useContext(CashflowStateContext);
  if (!ctx) {
    throw new Error("useCashflow must be used within a CashflowProvider");
  }
  return ctx;
}

export function useCashflowOptional(): CashflowStateValue | null {
  return useContext(CashflowStateContext);
}

export function useCashflowDispatch(): Dispatch<CashflowAction> {
  const ctx = useContext(CashflowDispatchContext);
  if (!ctx) {
    throw new Error(
      "useCashflowDispatch must be used within a CashflowProvider"
    );
  }
  return ctx;
}
